import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline'

import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AddSubPageIcon } from '@/components/icons/AddSubPageIcon'
import { RemoveSubPageIcon } from '@/components/icons/RemoveSubPageIcon'
import type { DocumentPage, FlattenedDocumentPage } from '@/lib/api/client'
import { LETTER_PAGE_HEIGHT } from '../DocumentTemplate'
import {
  useToggleSubpageVisibility,
  useMoveSubpage,
} from '@/services/queries/document_pages'

type SubpageControlsProps = {
  subPageId: string
  index: number
  isFirst: boolean
  isLast: boolean
  isHidden: boolean
  isReadOnly: boolean
  documentPage: FlattenedDocumentPage
  documentVersionId: string
  onAddSubpage: () => void
  onRemoveSubpage: (subPageId: string) => void
}

export const SubpageControls = ({
  subPageId,
  index,
  isFirst,
  isLast,
  isHidden,
  isReadOnly,
  documentPage,
  documentVersionId,
  onAddSubpage,
  onRemoveSubpage,
}: SubpageControlsProps) => {
  const { mutate: toggleSubpageVisibility } = useToggleSubpageVisibility()
  const { mutate: moveSubpage } = useMoveSubpage()

  if (isReadOnly) return null

  const handleToggleHide = () => {
    toggleSubpageVisibility({
      parentPageId: documentPage.id as string,
      subPageId,
      documentVersionId,
      parentPage: documentPage as unknown as DocumentPage,
    })
  }

  const handleMoveUp = () => {
    if (isFirst) return
    moveSubpage({
      parentPageId: documentPage.id as string,
      subPageId,
      direction: 'up',
      documentVersionId,
      parentPage: documentPage as unknown as DocumentPage,
    })
  }

  const handleMoveDown = () => {
    if (isLast) return
    moveSubpage({
      parentPageId: documentPage.id as string,
      subPageId,
      direction: 'down',
      documentVersionId,
      parentPage: documentPage as unknown as DocumentPage,
    })
  }

  return (
    <TooltipProvider>
      <div
        className="z-[10] flex flex-col gap-2 absolute top-0 right-0"
        style={{
          transform: 'translateX(120%)',
          top: index * LETTER_PAGE_HEIGHT,
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="p-1 h-auto rounded-full bg-white shadow-md hover:bg-primary-20/30 border border-gray-100 flex items-center justify-center"
              variant="ghost"
              onClick={onAddSubpage}
            >
              <AddSubPageIcon className="w-3 h-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-black text-white">
            Add section
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="p-1 h-auto rounded-full bg-white shadow-md hover:bg-primary-20/30 border border-gray-100 flex items-center justify-center"
              variant="ghost"
              onClick={handleToggleHide}
            >
              {isHidden ? (
                <EyeIcon className="w-3 h-3" />
              ) : (
                <EyeSlashIcon className="w-3 h-3" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-black text-white">
            {isHidden ? 'Show section' : 'Hide section'}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="p-1 h-auto rounded-full bg-white shadow-md hover:bg-primary-20/30 border border-gray-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              variant="ghost"
              onClick={handleMoveUp}
              disabled={isFirst}
            >
              <ArrowUpIcon className="w-3 h-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-black text-white">
            Move up
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="p-1 h-auto rounded-full bg-white shadow-md hover:bg-primary-20/30 border border-gray-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              variant="ghost"
              onClick={handleMoveDown}
              disabled={isLast}
            >
              <ArrowDownIcon className="w-3 h-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-black text-white">
            Move down
          </TooltipContent>
        </Tooltip>

        {!isFirst && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="p-1 h-auto rounded-full bg-white shadow-md hover:bg-red-50 border border-gray-100 flex items-center justify-center"
                variant="ghost"
                onClick={() => onRemoveSubpage(subPageId)}
              >
                <RemoveSubPageIcon className="w-3 h-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-black text-white">
              Remove section
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  )
}
